import React from 'react';
import { Pin, PinOff, EyeOff, Clock, Lock, Sparkles, Rocket } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { FREE_TOOL_KEYS } from './constants';
import { toast } from 'sonner';

// Grid Tool Card Component (Original Dashboard Design)
export const GridToolCard = ({ tool, isPinned, onToolClick, onTogglePin, onToggleHide, badge }) => {
  const Icon = tool.icon;
  const hasBadge = badge?.badges?.includes('in_progress');
  const badgeCount = badge?.count;

  return (
    <button
      onClick={() => onToolClick(tool)}
      className="p-4 sm:p-6 bg-white rounded-xl sm:rounded-2xl border border-gray-200 hover:shadow-lg transition-all text-left group relative"
    >
      {/* In Progress Badge */}
      {hasBadge && (
        <div className="absolute -top-2 -right-2 flex items-center gap-1 px-2 py-0.5 bg-amber-500 text-white text-xs font-medium rounded-full shadow-sm z-20">
          <Clock className="w-3 h-3" />
          {badgeCount ? `${badgeCount} in progress` : 'In Progress'}
        </div>
      )}

      {/* Pin button - TOP LEFT */}
      <button
        onClick={(e) => onTogglePin(tool.key, e)}
        className={cn(
          "absolute top-2 left-2 p-1.5 rounded-lg transition-all z-10",
          isPinned
            ? "bg-blue-100 text-blue-600"
            : "opacity-0 group-hover:opacity-100 hover:bg-gray-100 text-gray-400"
        )}
        title={isPinned ? "Unpin from top" : "Pin to top"}
      >
        {isPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
      </button>

      {/* Hide button - TOP RIGHT */}
      <button
        onClick={(e) => onToggleHide(tool.settingsKey, e)}
        className={cn(
          "absolute p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-gray-100 text-gray-400 transition-all z-10",
          hasBadge ? "top-8 right-2" : "top-2 right-2"
        )}
        title="Hide from dashboard"
      >
        <EyeOff className="w-4 h-4" />
      </button>

      <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br ${tool.color} rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform text-white`}>
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="font-semibold text-sm sm:text-base mb-1">{tool.name}</h3>
      <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">{tool.description}</p>
    </button>
  );
};

// List Tool Card Component (Categorized View) - WITH PREMIUM GATING
// Premium overlay now only shows on CLICK, not by default
export const ListToolCard = ({ tool, isPinned, onToolClick, onTogglePin, onToggleHide, badge, isPremium, isLocked, hasPremiumAccess }) => {
  const Icon = tool.icon;
  const hasBadge = badge?.badges?.includes('in_progress');
  const badgeCount = badge?.count;
  const isFree = FREE_TOOL_KEYS.includes(tool.key);
  const isComingSoon = tool.comingSoon === true;
  
  // User needs premium but doesn't have access - will show overlay on CLICK only
  const needsUpgrade = isPremium && !isFree && !hasPremiumAccess;
  
  // Premium badge shows for ALL premium tools for ALL users
  // VIP, Founder, Early Adopter users see the badge but get direct access
  // Standard users see the badge and get upgrade modal on click
  const showPremiumBadge = (isPremium && !isFree) || isComingSoon;

  // Handle click - block if coming soon
  const handleClick = () => {
    if (isComingSoon) {
      toast.info('Coming Soon! This feature is scheduled for release soon. Stay tuned!', {
        duration: 4000,
        icon: <Rocket className="w-5 h-5 text-indigo-500" />
      });
      return;
    }
    onToolClick(tool, needsUpgrade);
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        "group relative bg-white rounded-xl border border-gray-200 p-4 cursor-pointer overflow-hidden",
        "hover:shadow-lg hover:border-gray-300 transition-all duration-200",
        isPinned && "ring-2 ring-blue-100 border-blue-200",
        isComingSoon && "opacity-90"
      )}
    >
      {/* Coming Soon Diagonal Banners - Both corners, bigger text */}
      {isComingSoon && (
        <>
          {/* Left corner banner */}
          <div className="absolute -left-12 top-6 z-30 -rotate-45 transform">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-14 py-1.5 shadow-lg uppercase tracking-wide">
              Coming Soon
            </div>
          </div>
          {/* Right corner banner */}
          <div className="absolute -right-12 top-6 z-30 rotate-45 transform">
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-bold px-14 py-1.5 shadow-lg uppercase tracking-wide">
              Coming Soon
            </div>
          </div>
        </>
      )}

      {/* Premium Badge - Shows for ALL premium tools for ALL users, including Coming Soon */}
      {showPremiumBadge && (
        <div className="absolute -top-1 -right-1 z-20">
          <Badge className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-[9px] px-1.5 py-0.5 shadow-sm">
            <Sparkles className="w-2.5 h-2.5 mr-0.5" />
            Premium
          </Badge>
        </div>
      )}

      {/* In Progress Badge */}
      {hasBadge && !isComingSoon && (
        <div className="absolute -top-2 -right-2 flex items-center gap-1 px-2 py-0.5 bg-amber-500 text-white text-xs font-medium rounded-full shadow-sm z-20">
          <Clock className="w-3 h-3" />
          {badgeCount || ''}
        </div>
      )}

      {/* Pin button - TOP LEFT - Hidden for coming soon */}
      {!isComingSoon && (
        <button
          onClick={(e) => onTogglePin(tool.key, e)}
          className={cn(
            "absolute top-2 left-2 p-1.5 rounded-lg transition-all z-10",
            isPinned
              ? "bg-blue-100 text-blue-600"
              : "opacity-0 group-hover:opacity-100 hover:bg-gray-100 text-gray-400"
          )}
          title={isPinned ? "Unpin from top" : "Pin to top"}
        >
          {isPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
        </button>
      )}

      {/* Hide button - TOP RIGHT - Hidden for coming soon */}
      {!isComingSoon && (
        <button
          onClick={(e) => onToggleHide(tool.settingsKey, e)}
          className={cn(
            "absolute p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-gray-100 text-gray-400 transition-all z-10",
            hasBadge ? "top-6 right-2" : "top-2 right-2",
            showPremiumBadge && "top-5 right-2"
          )}
          title="Hide from dashboard"
        >
          <EyeOff className="w-4 h-4" />
        </button>
      )}

      <div className="flex flex-col items-center text-center mt-2">
        <div className={cn(
          "w-12 h-12 rounded-xl flex items-center justify-center mb-3",
          `bg-gradient-to-br ${tool.color}`,
          isComingSoon && "opacity-75"
        )}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <h3 className={cn(
          "font-medium text-gray-900 text-sm leading-tight",
          isComingSoon && "text-gray-700"
        )}>{tool.name}</h3>
        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{tool.description}</p>
      </div>
    </div>
  );
};
